import { ApiErrorMessages } from '@/components/element/ApiErrorMessages';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { useFixedAssetStatuses } from '@/hooks/api/1990_fixed_asset_statuses';
import { useInvalidateFixedAssets } from '@/hooks/api/2000_fixed_assets';
import { useUpdateFixedAsset } from '@/hooks/api/2020_update_fixed_asset';
import { useBase64FromImageFileInput } from '@/hooks/useBase64FromImageFileInput';
import { toastSuccess } from '@/lib/toast';
import { useCurrentFixedAsset } from '@/providers/FixedAssetProvider';
import { useLoading } from '@/providers/LoadingProvider';
import { useNavState } from '@/providers/NavStateProvider';
import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';
import { dateFormat } from '@/utils';
import { Japanese } from 'flatpickr/dist/l10n/ja';
import { useEffect } from 'react';
import Flatpickr from 'react-flatpickr';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';

type FormInputs = {
  identifier: string;
  id: number;
  name: string;
  description: string;
  quantity: number;
  start_date: string;
  image_file: string;
  fixed_asset_status_id: number;
};
export const FixedAssetEdit = () => {
  const { setTitle, setBreadcrumbs } = useNavState();
  useEffect(() => setTitle('Edit Fixed Asset'), []);
  useEffect(
    () =>
      setBreadcrumbs([
        { label: `Fixed Assets`, path: `/w/${_w_!.identifier}/finance/fixed_assets` },
        { label: `Fixed Asset Detail`, path: `/w/${_w_!.identifier}/finance/fixed_assets/${fixedAsset!.id}` },
      ]),
    []
  );

  const navigate = useNavigate();
  const { setShowLoading } = useLoading();
  const { _w_, me } = useCurrentWorkspace();
  const { toBase64FromFile, base64Content } = useBase64FromImageFileInput({ aspectRatio: 1 });
  const { fixedAsset } = useCurrentFixedAsset();
  const { data: fixedAssetStatuses } = useFixedAssetStatuses();
  const { mutate, data, isPending, error } = useUpdateFixedAsset();
  const { invalidate } = useInvalidateFixedAssets(_w_!.identifier);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: { identifier: _w_!.identifier, id: fixedAsset!.id },
  });

  useEffect(() => {
    if (fixedAsset) {
      setValue('name', fixedAsset.name);
      setValue('description', fixedAsset.description || '');
      setValue('quantity', fixedAsset.quantity);
      setValue('start_date', dateFormat({ date: fixedAsset.start_date }));
      setValue('fixed_asset_status_id', fixedAsset.fixed_asset_status.id);
    }
  }, [fixedAsset]);

  useEffect(() => {
    if (base64Content) setValue('image_file', base64Content);
  }, [base64Content]);

  useEffect(() => setShowLoading(isPending), [isPending]);

  useEffect(() => {
    if (data) {
      invalidate();
      toastSuccess('固定資産の変更が完了しました');
      navigate(-1);
    }
  }, [data]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    mutate(data);
  };

  if (fixedAsset == null) return <></>;

  return (
    <WorkspaceLayout>
      <div className="container-fluid py-4">
        <div className="row">
          <div className="col-12 col-lg-9 mx-auto">
            <div className="card mb-4" id="basic-info">
              <div className="card-header d-flex align-items-center">
                <div className="col-md-8">
                  <h5 className="mb-1">Edit Fixed Asset</h5>
                  <p className="text-xs mb-0">固定資産情報編集</p>
                </div>
              </div>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="card-body pt-0 row">
                  <ApiErrorMessages error={error} />

                  <div className="col-12 col-sm-4 col-md-3">
                    <div className="avatar avatar-xxl position-relative">
                      <img src={base64Content || fixedAsset!.image_file_url} alt="profile_image" className="w-100 border-radius-md shadow-sm" />
                      <input type="file" id="avater" accept="image/png, image/jpeg" onChange={toBase64FromFile} style={{ display: 'none' }} />
                      <input type="hidden" {...register('image_file', {})} />
                      <label className="btn btn-sm btn-icon-only bg-gradient-light position-absolute bottom-0 end-0 mb-n2 me-n2" htmlFor="avater">
                        <i
                          className="fa fa-pen top-0"
                          data-bs-toggle="tooltip"
                          data-bs-placement="top"
                          title=""
                          aria-hidden="true"
                          data-bs-original-title="Edit Image"
                          aria-label="Edit Image"
                        ></i>
                      </label>
                    </div>
                  </div>
                  <div className="col-12 col-sm-8 col-md-9 mt-4 mt-sm-0">
                    <div className="form-group">
                      <label>
                        Name<span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        maxLength={50}
                        className={`form-control ${errors.name && 'is-invalid'}`}
                        placeholder={'固定資産名'}
                        {...register('name', {
                          required: '入力して下さい。',
                        })}
                      />
                      {errors.name && <div className="text-xs text-danger mt-1">{errors.name.message}</div>}
                    </div>
                    <div className="form-group">
                      <label>Note</label>
                      <textarea className="form-control" maxLength={2000} rows={5} {...register('description')}></textarea>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="form-group">
                        <label>
                          Quantity<span className="text-danger">*</span>
                        </label>
                        <input
                          type="number"
                          min={1}
                          className={`form-control ${errors.quantity && 'is-invalid'}`}
                          placeholder={''}
                          {...register('quantity', {
                            required: '入力して下さい。',
                          })}
                        />
                        {errors.quantity && <div className="text-xs text-danger mt-1">{errors.quantity.message}</div>}
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="form-group">
                        <label>
                          Commencement<span className="text-danger">*</span>
                        </label>
                        <Controller
                          control={control}
                          name="start_date"
                          rules={{ required: '入力して下さい。' }}
                          render={({ field: { onChange, ...fieldProps } }) => (
                            <Flatpickr
                              className="form-control datepickr"
                              {...fieldProps}
                              onChange={(_, dateString) => onChange(dateString)}
                              options={{ locale: Japanese, dateFormat: 'Y/m/d' }}
                            />
                          )}
                        />
                        <div className="text-xs text-muted mt-1">固定資産を利用開始する（予定）日付を設定</div>
                        {errors.start_date && <div className="text-xs text-danger mt-1">{errors.start_date.message}</div>}
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="form-group">
                        <label>
                          Status<span className="text-danger">*</span>
                        </label>
                        <select className="form-control" required={true} {...register('fixed_asset_status_id')}>
                          {fixedAssetStatuses?.map((fas, i) => (
                            <option key={`fas-${fas.id}`} value={fas.id}>
                              {fas.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card-footer d-flex align-items-center justify-content-end pt-0">
                  <Link to="" className="btn btn-outline-secondary me-2" onClick={() => navigate(-1)}>
                    戻る
                  </Link>
                  <button className="btn bg-gradient-dark btn-icon">
                    <i className="fas fa-check-circle"></i>変更を確定する
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </WorkspaceLayout>
  );
};
